<template>
  <div>
    <DataTable
      :loading="!initialDataLoaded"
      v-model:filters="tableFilters"
      v-model:first="currentPage"
      v-model:sort-field="tableSortedBy"
      filter-display="menu"
      :value="normalizedBills"
      class="table-component__spread-paginator-label"
      responsive-layout="scroll"
      selection-mode="single"
      data-cy="bills-table"
      :paginator="true"
      :rows="20"
      :paginator-template="{
        '640px':
          'CurrentPageReport FirstPageLink PrevPageLink NextPageLink LastPageLink RowsPerPageDropdown',
        default:
          'CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown',
      }"
      :rows-per-page-options="[10, 20, 50]"
      current-page-report-template="Showing {first} to {last} of {totalRecords}"
      @rowSelect="onRowSelect"
      @sort="onTableSort"
    >
      <template #empty>
        {{ $t('account.pages.subPagePaymentTransactions.emptyVendorTable') }}
      </template>
      <Column
        field="formattedValuesForSorting.billStatusLabel"
        :header="$t('account.pages.subPagePaymentTransactions.status')"
        sortable
        :show-filter-match-modes="false"
        :filter-menu-class="$style['filter']"
      >
        <template #body="slotProps">
          <PaymentBillStatusBadge :bill="slotProps?.data" />
        </template>
        <template #filter="{ filterModel }">
          <ul>
            <li
              v-for="(status, index) in uniqueBillStatusLabels"
              :key="`filter-status-${encodeURI(status)}-${index}`"
            >
              <Checkbox
                v-model="filterModel.value"
                :input-id="`filter-status-${encodeURI(status)}-${index}`"
                :value="status"
              />
              <label :for="`filter-status-${encodeURI(status)}-${index}`">{{
                status
              }}</label>
            </li>
          </ul>
        </template>
        <template #filterapply="{ filterCallback }">
          <DSButton
            label="Apply"
            class="p-button-sm"
            @click="onFilterChange(filterCallback, 'Status')"
          />
        </template>
      </Column>
      <Column
        field="formattedValuesForSorting.createdAt"
        sortable
        :header="$t('account.pages.subPagePaymentTransactions.dateCreated')"
        data-type="date"
      >
        <template #body="slotProps">
          {{ dueDateToEasternDateTime(slotProps?.data.createdAt) }}
        </template>
        <template #filter="{ filterModel }">
          <Calendar
            v-model="filterModel.value"
            name="calendar"
            :manual-input="false"
            placeholder="yyyy-mm-dd"
            show-icon
          />
        </template>
        <template #filterapply="{ filterCallback }">
          <DSButton
            label="Apply"
            class="p-button-sm"
            @click="onFilterChange(filterCallback, 'Created Date')"
          />
        </template>
      </Column>
      <Column
        field="formattedValuesForSorting.id"
        sortable
        :header="
          $t('account.pages.subPagePaymentTransactions.invoiceReceiptId')
        "
      >
        <template #body="slotProps">
          {{ getShortBillId(slotProps?.data.id).toUpperCase() }}
        </template>
        <template #filter="{ filterModel }">
          <InputText
            v-model="filterModel.value"
            placeholder="Search by ID"
            @input="filterModel.value = filterModel.value.toUpperCase()"
          />
        </template>
        <template #filterapply="{ filterCallback }">
          <DSButton
            label="Apply"
            class="p-button-sm"
            @click="onFilterChange(filterCallback, 'Id')"
          />
        </template>
      </Column>
      <Column
        field="formattedValuesForSorting.vendorName"
        sortable
        :header="$t('account.pages.subPagePaymentTransactions.vendor')"
      >
        <template #body="slotProps">
          {{ getVendorName(slotProps?.data) }}
        </template>
        <template #filter="{ filterModel }">
          <InputText
            v-model="filterModel.value"
            placeholder="Search by vendor"
          />
        </template>
        <template #filterapply="{ filterCallback }">
          <DSButton
            label="Apply"
            class="p-button-sm"
            @click="onFilterChange(filterCallback, 'Vendor')"
          />
        </template>
      </Column>
      <Column
        field="formattedValuesForSorting.amount"
        :class="$style['wide-column']"
        sortable
        :header="$t('account.pages.subPagePaymentTransactions.amount')"
        data-type="numeric"
      >
        <template #body="slotProps">
          {{
            formatMoney(
              slotProps?.data.amountCents,
              slotProps?.data.payOutCurrency,
            )
          }}
          {{ slotProps?.data.payOutCurrency }}
        </template>
        <template #filter="{ filterModel }">
          <InputNumber
            v-model="filterModel.value"
            mode="decimal"
            locale="en-US"
            :min-fraction-digits="2"
          />
        </template>
        <template #filterapply="{ filterCallback }">
          <DSButton
            label="Apply"
            class="p-button-sm"
            @click="onFilterChange(filterCallback, 'Amount')"
          />
        </template>
      </Column>
      <Column
        field="formattedValuesForSorting.dueDate"
        sortable
        :header="$t('account.pages.subPagePaymentTransactions.dueDate')"
        data-type="date"
      >
        <template #body="slotProps">
          {{ dueDate(slotProps?.data) }}
        </template>
        <template #filter="{ filterModel }">
          <Calendar
            v-model="filterModel.value"
            name="calendar"
            :manual-input="false"
            placeholder="yyyy-mm-dd"
            show-icon
          />
        </template>
        <template #filterapply="{ filterCallback }">
          <DSButton
            label="Apply"
            class="p-button-sm"
            @click="onFilterChange(filterCallback, 'Due Date')"
          />
        </template>
      </Column>
      <Column
        field="formattedValuesForSorting.billTypeLabel"
        :header="$t('account.pages.subPagePaymentTransactions.billType')"
        sortable
        :show-filter-match-modes="false"
        :filter-menu-class="$style['filter']"
      >
        <template #body="slotProps">
          {{ billTypeLabel[slotProps?.data.type] }}
        </template>
        <template #filter="{ filterModel }">
          <ul>
            <li
              v-for="(label, index) in uniqueBillTypeLabels"
              :key="`filter-status-${encodeURI(status)}-${index}`"
            >
              <Checkbox
                v-model="filterModel.value"
                :input-id="`filter-upload-type-${encodeURI(label)}-${index}`"
                :value="label"
              />
              <label :for="`filter-upload-type-${encodeURI(label)}-${index}`">{{
                label
              }}</label>
            </li>
          </ul>
        </template>
        <template #filterapply="{ filterCallback }">
          <DSButton
            label="Apply"
            class="p-button-sm"
            @click="onFilterChange(filterCallback, 'uploadType')"
          />
        </template>
      </Column>
    </DataTable>
    <PaymentBillDetailsActionsModal
      ref="rowItemDetailsModal"
      @newPayment="this.$emit('newPayment')"
      @done="handleCloseBillDetails"
    />
  </div>
</template>
<script>
import { FilterMatchMode, FilterOperator } from 'primevue/api';
import { mapGetters } from 'vuex';
import { useContextRoot } from '@/utils/context-root';
import { getBrowserWidth } from '@/utils/browser';
import billComposables from '@/composables/bills';
import analytics, { SEGMENT_EVENTS } from '@/utils/analytics';
import { getShortBillId, dueDateToEasternDateTime } from '@/utils/payments';
import { BillPaymentMethods, BillStatus } from '@/data/payments';
import { formatMoney } from '@/utils/currency';
import { RouteNames } from '@/data/routes';

import InputText from 'primevue/inputtext';
import Calendar from 'primevue/calendar';
import DataTable from 'primevue/datatable';
import InputNumber from 'primevue/inputnumber';
import Column from 'primevue/column';
import Checkbox from 'primevue/checkbox';
import DSButton from 'primevue/button';
import PaymentBillStatusBadge from '@/pages/payments/components/PaymentBillStatusBadge.vue';
import PaymentBillDetailsActionsModal from '@/pages/payments/components/PaymentBillDetailsActionsModal/index.vue';
import { BillType, billTypeLabel } from '@/@types/bill.types';
import { TransactionType } from '@/types/transaction.types';

export default {
  components: {
    InputText,
    InputNumber,
    Calendar,
    PaymentBillStatusBadge,
    DataTable,
    Column,
    PaymentBillDetailsActionsModal,
    Checkbox,
    DSButton,
  },
  emits: ['newPayment'],
  setup(props, context) {
    const ctxRoot = useContextRoot();
    const { billStatusData } = billComposables(context, ctxRoot);
    return { billStatusData };
  },
  data() {
    const initialTableFilters = {
      'formattedValuesForSorting.billStatusLabel': {
        value: null,
        matchMode: FilterMatchMode.IN,
      },
      'formattedValuesForSorting.vendorName': {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
      },
      'formattedValuesForSorting.dueDate': {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }],
      },
      'formattedValuesForSorting.createdAt': {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }],
      },
      'formattedValuesForSorting.billTypeLabel': {
        value: null,
        matchMode: FilterMatchMode.IN,
      },
      'formattedValuesForSorting.id': {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
      },
      'formattedValuesForSorting.amount': {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
      },
    };

    return {
      tableFilters: initialTableFilters,
      currentPage: 0,
      tableSortedBy: undefined,
      uniqueBillStatusLabels: [],
      uniqueBillTypeLabels: [],
      billTypeLabel,
      initialDataLoaded: false,
    };
  },
  computed: {
    ...mapGetters([
      'vendors',
      'bills',
      'business',
      'businessId',
      'selectedBill',
      'contracts',
    ]),
    normalizedBills() {
      return this.bills.map((bill) => {
        // Get a list of all the unique status labels
        const formattedBillLabel = this.getFormattedBillStatusLabel(bill.id);

        if (!this.uniqueBillStatusLabels.includes(formattedBillLabel)) {
          this.uniqueBillStatusLabels.push(formattedBillLabel);
        }

        // Get a list of all the unique bill types
        const formattedBillTypeLabel = billTypeLabel[bill.type];

        if (!this.uniqueBillTypeLabels.includes(formattedBillTypeLabel)) {
          this.uniqueBillTypeLabels.push(formattedBillTypeLabel);
        }

        return {
          ...bill,
          formattedValuesForSorting: {
            billStatusLabel: formattedBillLabel,
            vendorName: this.getVendorName(bill),
            dueDate: bill.dueDate
              ? this.convertDateToFilters(bill.dueDate)
              : null,
            createdAt: this.convertDateToFilters(bill.createdAt),
            billTypeLabel: formattedBillTypeLabel,
            id: getShortBillId(bill.id).toUpperCase(),
            amount: bill.amountCents / 100,
          },
          vendor: this.vendors.find((v) => {
            return v.id === bill.vendorId;
          }),
        };
      });
    },
  },
  mounted() {
    this.fetchBill();
    this.fetchAdditionalBillData();
  },
  watch: {
    $route() {
      this.fetchBill();
    },
  },
  methods: {
    getShortBillId,
    dueDateToEasternDateTime,
    formatMoney,
    existingContract(billId) {
      return this.contracts.find((contract) => contract.billId === billId);
    },
    async fetchBill() {
      const { billId } = this.$route?.params ?? {};
      if (billId && billId !== 'new') {
        await this.$store.dispatchApiAction('GET_BILL', {
          id: billId,
        });
        if (this.selectedBill?.id) {
          this.$refs.rowItemDetailsModal.open(this.selectedBill);
        } else {
          this.handleCloseBillDetails();
        }
      }
    },
    handleCloseBillDetails() {
      this.$router.replace({
        name: RouteNames.HOME.INITIAL,
        params: {},
      });
    },
    onTableSort(e) {
      const dataTypes = {
        'formattedValuesForSorting.billStatusLabel': 'Status',
        'formattedValuesForSorting.vendorName': 'Vendor',
        'formattedValuesForSorting.dueDate': 'Due Date',
        'formattedValuesForSorting.createdAt': 'Created Date',
        'formattedValuesForSorting.billTypeLabel': 'Bill Type',
        'formattedValuesForSorting.id': 'Id',
        'formattedValuesForSorting.amount': 'Amount',
      };

      analytics.track(SEGMENT_EVENTS.fe_sorting_button_clicked, {
        dataType: dataTypes[e.sortField],
        sortingType: e.sortOrder === 1 ? 'A-Z' : 'Z-A',
      });
    },
    onFilterChange(applyFilters, type) {
      applyFilters();
      analytics.track(SEGMENT_EVENTS.fe_filtering_button_clicked, {
        dataType: type,
      });
    },
    /** @public */
    resetFilters() {
      this.currentPage = 0;
      this.tableSortedBy = undefined;
      this.tableFilters = this.initialTableFilters;
      analytics.track(SEGMENT_EVENTS.fe_clear_filter_button_clicked);
    },
    getFormattedBillStatusLabel(billId) {
      const billContract = this.existingContract(billId);

      const statusData = this.billStatusData(
        billId,
        billContract,
        undefined,
        true,
      );
      const statusLabel =
        statusData && this.$t(statusData?.milestoneInProgress.label);

      return statusLabel;
    },
    async onRowSelect(selectedRowItem) {
      let currentBill = selectedRowItem.data;

      if (currentBill.id) {
        await this.$router.push({
          name: RouteNames.HOME.INITIAL,
          params: { billId: currentBill.id },
        });
        currentBill = this.selectedBill;
      }
      if (currentBill.paymentMethod !== BillPaymentMethods.BNPL) {
        analytics.track(SEGMENT_EVENTS.CLICKED_BILL_DETAILS, {
          businessId: this.businessId,
          businessName: this.business.name,
          billId: currentBill.id,
          shortBillId: getShortBillId(currentBill.id),
        });
      }
      if (currentBill.paymentMethod === BillPaymentMethods.BNPL) {
        analytics.track(SEGMENT_EVENTS.CLICKED_BNPL_BILL_DETAILS, {
          businessId: this.businessId,
          businessName: this.business.name,
          billId: currentBill.id,
          uploadType: currentBill.type,
          shortBillId: getShortBillId(currentBill.id),
        });
      }

      analytics.track(SEGMENT_EVENTS.if_user_view_submitted_bill, {
        type: billTypeLabel[this.selectedBill.type],
        browserWidth: getBrowserWidth(),
      });
    },
    convertDateToFilters(date) {
      return new Date(date.replace(/-/g, '/').replace(/T.+/, ''));
    },
    getVendorName(bill) {
      if (!bill) return '-';

      switch (bill.type) {
        case BillType.INVOICE:
          return bill.beneficiaryName || '-';
        case BillType.RECEIPT:
          // It seems that along the beneficiary name, when the status is cancelled, it also gets appended the
          // cancellation reason. E.G.: "Vendor Name; Cancellation Reason"
          // We only want to display the vendor name in this case
          if (bill.status === BillStatus.CANCELLED && bill.memo) {
            return bill.memo.split(';')[0];
          } else {
            return bill.receiptVendorName ?? '-';
          }
        case BillType.CASH_ADVANCE:
          return '-';
        default:
          return '-';
      }
    },
    dueDate(bill) {
      return bill.dueDate ? dueDateToEasternDateTime(bill.dueDate) : '-';
    },
    async fetchAdditionalBillData() {
      const promises = this.bills.map((bill) =>
        this.fetchBNPLandContractAndTransactions(bill),
      );

      if (this.bills?.length) {
        promises.push(
          this.$store.dispatchApiAction('FETCH_TRANSACTIONS', {
            type: TransactionType.VENDOR_INVOICE,
            businessId: this.businessId,
            advanceId: this.bills
              .filter((bill) => bill.advanceId)
              .map((bill) => bill.advanceId)
              .join(','),
            limit: 200,
          }),
        );
      }

      await Promise.allSettled(promises);
      this.initialDataLoaded = true;
    },
    async fetchBNPLandContractAndTransactions(bill) {
      const billContract = this.existingContract(bill.id);
      const diligenceApproved = !!bill.bnplDetails?.diligenceApprovedAt;

      // get contract
      if (!billContract && diligenceApproved) {
        await this.$store.dispatchApiAction('GET_BNPL_CONTRACT', {
          billId: bill.id,
        });
      }
    },
  },
};
</script>

<style lang="less" module>
.filter {
  [class^='p-column-filter-constraint'] {
    padding-bottom: 0;
  }

  li + li {
    margin-top: 10px;
  }

  div + label {
    margin-left: 10px;
  }

  label {
    cursor: pointer;
  }
}

.wide-column {
  width: 182px;
}
</style>
